import {
  AgentSupportUserType,
  InvalidAgentSupportUserType,
  UnvalidatedAgentSupportUserType,
  ValidAgentSupportUserType,
} from 'Users/models/User/Agent/AgentSupportUserType';
import * as A from 'Users/models/User/Agent/AgentUserType';
import {
  FieldSalesLeaderUserType,
  InvalidFieldSalesLeaderUserType,
  UnvalidatedFieldSalesLeaderUserType,
  ValidFieldSalesLeaderUserType,
} from 'Users/models/User/Agent/FieldSalesLeaderUserType';
import {
  FieldVicePresidentUserType,
  InvalidFieldVicePresidentUserType,
  UnvalidatedFieldVicePresidentUserType,
  ValidFieldVicePresidentUserType,
} from 'Users/models/User/Agent/FieldVicePresidentUserType';
import {
  InvalidVIPUserType,
  UnvalidatedVIPUserType,
  ValidVIPUserType,
  VIPUserType,
} from 'Users/models/User/Agent/VipUserType';

export enum AgentRole {
  Undetermined,
  Agent,
  AgentSupport,
  VIP,
  FieldVicePresident,
  FieldSalesLeader,
}

export type AgentUserTypeValue = ValidAgentUserType['value'] | InvalidAgentUserType['value'];

export class UndeterminedAgentUserType {
  public readonly key = 'UndeterminedAgentUserType';
  public readonly value: never;

  constructor(public readonly agentRole = AgentRole.Undetermined) {}
}

export class InvalidAgentUserType {
  public readonly key = 'InvalidAgentUserType';

  constructor(
    public readonly value:
      | InvalidFieldSalesLeaderUserType
      | InvalidFieldVicePresidentUserType
      | InvalidVIPUserType
      | UndeterminedAgentUserType
      | UnvalidatedFieldSalesLeaderUserType
      | UnvalidatedFieldVicePresidentUserType
      | UnvalidatedVIPUserType
      | A.UnvalidatedAgentUserType
      | A.InvalidAgentUserType
      | UnvalidatedAgentSupportUserType
      | InvalidAgentSupportUserType
  ) {}
}

const isValidAgentUserType = (
  agentUserTypeValue: AgentUserTypeValue
): agentUserTypeValue is ValidAgentUserType['value'] =>
  agentUserTypeValue.key === 'ValidFieldSalesLeaderUserType' ||
  agentUserTypeValue.key === 'ValidVIPUserType' ||
  agentUserTypeValue.key === 'ValidFieldVicePresidentUserType' ||
  agentUserTypeValue.key === 'ValidAgentUserType' ||
  agentUserTypeValue.key === 'ValidAgentSupportUserType';

export class ValidAgentUserType {
  public static create(agentUserType: AgentUserTypeValue) {
    return isValidAgentUserType(agentUserType)
      ? new ValidAgentUserType(agentUserType)
      : new InvalidAgentUserType(agentUserType);
  }

  public readonly key = 'ValidAgentUserType';

  constructor(
    public readonly value:
      | ValidVIPUserType
      | ValidFieldSalesLeaderUserType
      | ValidFieldVicePresidentUserType
      | A.ValidAgentUserType
      | ValidAgentSupportUserType
  ) {}
}

export type AgentUserType = ValidAgentUserType | InvalidAgentUserType | UndeterminedAgentUserType;

type AgentUserTypeOptions<TReturn> = {
  Undetermined: () => TReturn;
  VIP: (vipUserType: VIPUserType) => TReturn;
  Agent: (agentUserType: A.AgentUserType) => TReturn;
  AgentSupport: (agentSupportUserType: AgentSupportUserType) => TReturn;
  FieldSalesLeader: (fieldSalesLeaderUserType: FieldSalesLeaderUserType) => TReturn;
  FieldVicePresident: (fieldVicePresidentUserType: FieldVicePresidentUserType) => TReturn;
};

export const matchAgentUserType = <TReturn>(
  agentUserType: InvalidAgentUserType['value'] | ValidAgentUserType['value'],
  options: AgentUserTypeOptions<TReturn>
): TReturn => {
  switch (agentUserType.key) {
    case 'UndeterminedAgentUserType':
      return options.Undetermined();
    case 'UnvalidatedAgentSupportUserType':
    case 'InvalidAgentSupportUserType':
    case 'ValidAgentSupportUserType':
      return options.AgentSupport(agentUserType);
    case 'UnvalidatedAgentUserType':
    case 'InvalidAgentUserType':
    case 'ValidAgentUserType':
      return options.Agent(agentUserType);
    case 'UnvalidatedFieldSalesLeaderUserType':
    case 'InvalidFieldSalesLeaderUserType':
    case 'ValidFieldSalesLeaderUserType':
      return options.FieldSalesLeader(agentUserType);
    case 'UnvalidatedVIPUserType':
    case 'InvalidVIPUserType':
    case 'ValidVIPUserType':
      return options.VIP(agentUserType);
    case 'UnvalidatedFieldVicePresidentUserType':
    case 'InvalidFieldVicePresidentUserType':
    case 'ValidFieldVicePresidentUserType':
      return options.FieldVicePresident(agentUserType);
  }
};

type ValidAgentUserTypeOptions<TReturn> = {
  VIP: (vipUserType: ValidVIPUserType) => TReturn;
  Agent: (agentUserType: A.ValidAgentUserType) => TReturn;
  AgentSupport: (agentSupportUserType: ValidAgentSupportUserType) => TReturn;
  FieldSalesLeader: (fieldSalesLeaderUserType: ValidFieldSalesLeaderUserType) => TReturn;
  FieldVicePresident: (fieldVicePresidentUserType: ValidFieldVicePresidentUserType) => TReturn;
};

export const matchValidAgentUserType = <TReturn>(
  agentUserType: ValidAgentUserType['value'],
  options: ValidAgentUserTypeOptions<TReturn>
): TReturn => {
  switch (agentUserType.key) {
    case 'ValidAgentSupportUserType':
      return options.AgentSupport(agentUserType);
    case 'ValidAgentUserType':
      return options.Agent(agentUserType);
    case 'ValidFieldSalesLeaderUserType':
      return options.FieldSalesLeader(agentUserType);
    case 'ValidVIPUserType':
      return options.VIP(agentUserType);
    case 'ValidFieldVicePresidentUserType':
      return options.FieldVicePresident(agentUserType);
  }
};
